import { Component, OnInit, TemplateRef } from '@angular/core';
import { ConfigService } from '../../../services/config.service';
import { FeedbackService } from '../../../services/feedback.service';
import * as moment from 'moment';
import { UserService } from '../../../services/user.service';
import { UserPersonInfo, UserInfo } from '../../../models/identity';
import { ChatMessage } from '../../../models/chat/chat-message.model';
import { ChatParticipant } from '../../../models/chat/chat-participant.model';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { saveAs } from 'file-saver';
import { NgxItem } from '../../../models/common/ngx-item.model';

@Component({
  selector: 'app-feedback-manage',
  templateUrl: './feedback-manage.component.html',
  styleUrls: ['./feedback-manage.component.scss'],
})
export class FeedbackManageComponent implements OnInit {
  public defaultPhoto: string =
    '../../../../assets/images/placeholder_avatar.png';
  public mainChatParticipant: ChatParticipant = {};
  public selectedParticipant: ChatParticipant = {};
  public isAnonymousMode = false;
  public bsValue = new Date();
  public bsRangeValue: Array<Date> = [];
  public minDate = new Date();
  public isLeaverMode = false;
  public isSpinnerEnable: boolean;

  public mainParticipants: Array<UserInfo> = [];
  public mainParticipantsSelectionItems: Array<NgxItem> = [];
  public additionalParticipants: Array<UserInfo> = [];

  modalRef: BsModalRef;

  private readonly anonymPhoto: string =
    '../../../assets/images/svg/anonymous.svg';
  private mainMessages: Array<ChatMessage> = [];
  private anonymousMessages: Array<ChatMessage> = [];
  private messageToDelete: ChatMessage = {};

  constructor(
    private readonly configService: ConfigService,
    private readonly userService: UserService,
    private readonly feedbackService: FeedbackService,
    private bsModalService: BsModalService
  ) {}

  ngOnInit() {
    this.minDate.setMonth(this.minDate.getMonth() - 1);
    this.bsRangeValue = [this.minDate, this.bsValue];
  }

  setSpinnerEnabled(isEnabled: boolean) {
    this.isSpinnerEnable = isEnabled;
  }

  getAnonymousMessage() {
    this.isAnonymousMode = true;
  }

  getEmployeeReport() {
    this.setSpinnerEnabled(true);
    this.feedbackService
      .getChatReport(
        this.bsRangeValue[0],
        this.bsRangeValue[1],
        this.mainChatParticipant.id
      )
      .subscribe((data) => {
        saveAs(
          data,
          `FBChat_${
            this.mainChatParticipant && this.mainChatParticipant.gpid
              ? this.mainChatParticipant.gpid
              : ''
          }_${this.localeTime(new Date())}.xlsx`
        );
        this.setSpinnerEnabled(false);
      });
  }

  public get displayMessages(): Array<ChatMessage> {
    if (this.isAnonymousMode) {
      return this.applyFilterToMessages(this.anonymousMessages);
    } else {
      return this.applyFilterToMessages(this.mainMessages);
    }
  }

  public localeTime(source: Date) {
    const offset = moment().utcOffset();
    return moment
      .utc(source)
      .utcOffset(offset)
      .locale('en')
      .format('H:mm | D MMMM');
  }

  public clearMainParticipantFilter() {
    this.mainParticipants = [];
    this.mainParticipantsSelectionItems = [];
    this.additionalParticipants = [];
    this.anonymousMessages = [];
    this.mainMessages = [];
    this.anonymousMessages = [];
    this.mainChatParticipant = {};
    this.selectedParticipant = {};
  }

  public selectedMainParticipantFilter(employeeId: number) {
    this.mainChatParticipant = this.getEmployeeById(
      this.mainParticipants,
      employeeId
    );
    this.getUserPicture(this.mainChatParticipant);
    this.feedbackService
      .getContactedParticipant(
        this.mainChatParticipant.id,
        this.bsRangeValue[0].toDateString(),
        this.bsRangeValue[1].toDateString()
      )
      .subscribe((data) => {
        this.additionalParticipants = data.map((d) => this.mapToUserInfo(d));
        this.additionalParticipants.forEach((user) =>
          this.getUserPicture(user)
        );
        this.selectedParticipant = {};
        this.mainMessages = [];
      });
    this.feedbackService
      .getEmployeeAnonymousMessages(
        this.mainChatParticipant.id,
        this.bsRangeValue[0].toDateString(),
        this.bsRangeValue[1].toDateString()
      )
      .subscribe((data) => {
        this.anonymousMessages = data;
      });
  }

  public setDataFilter(value: Date[]) {
    this.bsRangeValue = value;
    if (this.mainChatParticipant && this.mainChatParticipant.id) {
      this.selectedMainParticipantFilter(this.mainChatParticipant.id);
    }
  }

  public typedMainParticipantUnitFilter(pattern: string) {
    if (this.checkIfPatternReady(pattern)) {
      this.userService
        .getBySearchPattern(pattern, this.isLeaverMode)
        .subscribe((data) => {
          this.mainParticipants = data.map((d) => this.mapToUserInfo(d));
          this.mainParticipantsSelectionItems = this.mainParticipants.map(
            (p) => ({
              id: p.id,
              text: this.isLeaverMode
                ? p.gpid
                : `${p.displayName}${p.gpid ? ` - ${p.gpid}` : ''}`,
            })
          );
        });
    }
  }

  public openApproveModal(
    modalTemplate: TemplateRef<any>,
    message: ChatMessage
  ) {
    this.messageToDelete = message;
    this.modalRef = this.bsModalService.show(modalTemplate);
  }

  public clearAdditionalParticipantFilter() {
    this.additionalParticipants = [];
  }

  public selectedAdditionalParticipantFilter(employeeId: number) {
    this.isAnonymousMode = false;
    this.selectedParticipant = this.getEmployeeById(
      this.additionalParticipants,
      employeeId
    );
    this.getUserPicture(this.selectedParticipant);
    this.loadMessages();
  }

  public deleteMessage() {
    this.feedbackService
      .deleteMessage(this.messageToDelete.id)
      .subscribe((x) => {
        this.modalRef.hide();
        this.deleteLocalMessage(this.messageToDelete);
      });
  }

  public getDisplayedPhoto(message: ChatMessage) {
    if (this.isAnonymousMode) {
      return this.anonymPhoto;
    }
    return this.getMessageAutorPhoto(message);
  }

  private getMessageAutorPhoto(message: ChatMessage) {
    if (message.personId === this.selectedParticipant.personId) {
      return this.selectedParticipant.employeePhoto;
    } else if (message.personId === this.mainChatParticipant.personId) {
      return this.mainChatParticipant.employeePhoto;
    }
    return this.defaultPhoto;
  }

  public checkMineMessage(sourceId: number): boolean {
    return this.mainChatParticipant.personId === sourceId;
  }

  private deleteLocalMessage(message: ChatMessage) {
    this.mainMessages = this.mainMessages.filter(
      (msg) => msg.id !== message.id
    );
    this.anonymousMessages = this.anonymousMessages.filter(
      (msg) => msg.id !== message.id
    );
  }

  private loadMessages() {
    this.feedbackService
      .getParticipantstMessages(
        this.mainChatParticipant.id,
        this.selectedParticipant.id,
        this.bsRangeValue[0].toDateString(),
        this.bsRangeValue[1].toDateString()
      )
      .subscribe((data) => {
        this.mainMessages = data;
      });
  }

  private checkIfPatternReady(pattern: string): boolean {
    return (
      pattern.length >= this.configService.configData.minimumSearchStringLength
    );
  }

  private getEmployeeById(
    employees: Array<UserInfo>,
    id: number
  ): UserPersonInfo {
    return employees.find((emp) => emp.id === id);
  }

  private applyFilterToMessages(
    messages: Array<ChatMessage>
  ): Array<ChatMessage> {
    return this.messageOrderComparation(messages);
  }

  private getUserPicture(source: UserInfo) {
    this.feedbackService.getImage(source.id).subscribe((blob) => {
      this.configService.createImageFromBlob(blob, source);
    });
  }

  private messageOrderComparation(
    currentChatParticipantMessages: Array<ChatMessage>
  ): Array<ChatMessage> {
    return currentChatParticipantMessages.sort((leftSide, rightSide) => {
      if (leftSide.createdDate < rightSide.createdDate) {
        return -1;
      }
      if (leftSide.createdDate > rightSide.createdDate) {
        return 1;
      }
      return 0;
    });
  }

  private mapToUserInfo(source: UserPersonInfo): UserInfo {
    return {
      id: source.id,
      displayName: source.name,
      personId: source.personId,
      gpid: source.gpid,
      employeePhoto: source.employeePhoto,
      isActive: source.isActive,
      position: source.position,
      businessUnit: source.businessUnit,
    } as UserInfo;
  }
}
